// Chinese Checkers engine (pure), two players on the 121-hole star. You start with 10 marbles in the bottom point of
// the star and race them into the top point, where the computer's start; it races the other way. A move is either one
// step into a neighboring empty hole, or a chain of jumps: over one marble (anyone's) next to you into the empty hole
// straight beyond, as many times as you like. Nothing is captured. You win when the top point is full and at least one
// marble in it is yours, so a marble left at home can't block you. Spec: .wiki/wiki/topics/game-later-candidates.md §2
// (2-player default).
//
// Holes use cube coordinates (q, r, s = -q - r), r from -8 at the top to 8 at the bottom. The star is two big
// triangles laid over each other. Holes are numbered row by row from the top, left to right.

export type Side = "you" | "cpu"
export type Level = "easy" | "medium" | "hard"
export const LEVELS: Level[] = ["easy", "medium", "hard"]

export interface Hole { q: number; r: number }
const inStar = (q: number, r: number): boolean => {
  const s = -q - r
  return (q >= -4 && r >= -4 && s >= -4) || (q <= 4 && r <= 4 && s <= 4)
}
export const HOLES: Hole[] = (() => {
  const out: Hole[] = []
  for (let r = -8; r <= 8; r++) for (let q = -8; q <= 8; q++) if (inStar(q, r)) out.push({ q, r })
  return out
})()
const INDEX = new Map(HOLES.map((h, n) => [`${h.q},${h.r}`, n]))
export const at = (q: number, r: number): number => INDEX.get(`${q},${r}`) ?? -1
const DIRS: [number, number][] = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, -1], [-1, 1]]
/** For each hole, its neighbor and the hole beyond it in each direction (-1 off the board). */
const NEAR = HOLES.map(({ q, r }) => DIRS.map(([dq, dr]) => [at(q + dq, r + dr), at(q + 2 * dq, r + 2 * dr)] as [number, number]))

/** The bottom point (your start, the computer's goal) and the top point (the other way round). */
export const BOTTOM = HOLES.flatMap((h, n) => (h.r >= 5 ? [n] : []))
export const TOP = HOLES.flatMap((h, n) => (h.r <= -5 ? [n] : []))
const goal = (side: Side): number[] => (side === "you" ? TOP : BOTTOM)
const APEX: Record<Side, Hole> = { you: { q: 4, r: -8 }, cpu: { q: -4, r: 8 } }
const dist = (n: number, to: Hole): number => {
  const h = HOLES[n]!
  return Math.max(Math.abs(h.q - to.q), Math.abs(h.r - to.r), Math.abs(h.q + h.r - to.q - to.r))
}

export interface Move { from: number; to: number; path: number[] } // path: the holes landed on, in order
export interface CCState {
  board: (Side | null)[] // 121
  turn: Side
  over: null | { winner: Side | "draw"; why: string }
  moves: number // your moves
  plies: number
}
export const MAX_PLIES = 400 // 200 moves each, then the one closer to home wins

export function newGame(): CCState {
  const board: (Side | null)[] = HOLES.map((_, n) => (BOTTOM.includes(n) ? "you" : TOP.includes(n) ? "cpu" : null))
  return { board, turn: "you", over: null, moves: 0, plies: 0 }
}

/** Every hole the marble on `from` can reach, with the route there (the shortest chain of jumps, or one step). */
export function movesFrom(board: (Side | null)[], from: number): Move[] {
  const out: Move[] = []
  for (const [step] of NEAR[from]!) if (step >= 0 && !board[step]) out.push({ from, to: step, path: [step] })
  // Jumps, breadth first, so each hole is reached by its shortest chain.
  const seen = new Set([from])
  let frontier: { at: number; path: number[] }[] = [{ at: from, path: [] }]
  while (frontier.length) {
    const next: typeof frontier = []
    for (const { at: a, path } of frontier) {
      for (const [over, land] of NEAR[a]!) {
        if (over < 0 || land < 0 || !board[over] || (board[land] && land !== from) || seen.has(land)) continue
        seen.add(land)
        const p = [...path, land]
        out.push({ from, to: land, path: p })
        next.push({ at: land, path: p })
      }
    }
    frontier = next
  }
  return out
}

export function legalMoves(s: CCState, side: Side = s.turn): Move[] {
  if (s.over) return []
  return s.board.flatMap((x, n) => (x === side ? movesFrom(s.board, n) : []))
}

const done = (board: (Side | null)[], side: Side): boolean => goal(side).every((n) => board[n]) && goal(side).some((n) => board[n] === side)
export const progress = (board: (Side | null)[], side: Side): number => board.reduce((k, x, n) => k + (x === side ? dist(n, APEX[side]) : 0), 0)
export const home = (board: (Side | null)[], side: Side): number => goal(side).filter((n) => board[n] === side).length

export function play(s: CCState, m: Move): CCState {
  const board = s.board.slice()
  board[m.to] = board[m.from]!
  board[m.from] = null
  const side = s.turn
  const next: CCState = { board, turn: side === "you" ? "cpu" : "you", over: null, moves: s.moves + (side === "you" ? 1 : 0), plies: s.plies + 1 }
  if (done(board, side)) next.over = { winner: side, why: "home" }
  else if (next.plies >= MAX_PLIES) {
    const a = progress(board, "you"), b = progress(board, "cpu")
    next.over = { winner: a < b ? "you" : b < a ? "cpu" : "draw", why: "moves" }
  }
  return next
}

// ---- the computer ------------------------------------------------------------------------------------------------

/** Score from the computer's side: how much closer its marbles are to their far corner than yours, with a penalty
 *  for a marble left far behind (stragglers lose races). */
export function evaluate(board: (Side | null)[]): number {
  let v = progress(board, "you") - progress(board, "cpu")
  let worstYou = 0, worstCpu = 0
  board.forEach((x, n) => {
    if (x === "you") worstYou = Math.max(worstYou, dist(n, APEX.you))
    else if (x === "cpu") worstCpu = Math.max(worstCpu, dist(n, APEX.cpu))
  })
  v += (worstYou - worstCpu) * 0.6
  return v
}

const WIN = 10_000
const gain = (s: CCState, m: Move): number => dist(m.from, APEX[s.turn]) - dist(m.to, APEX[s.turn])

/** Moves worth searching: forward ones first, and only the best few at depth. */
function candidates(s: CCState, limit: number): Move[] {
  const all = legalMoves(s)
  all.sort((a, b) => gain(s, b) - gain(s, a))
  return all.slice(0, limit)
}

function search(s: CCState, depth: number, alpha: number, beta: number): number {
  if (s.over) return s.over.winner === "cpu" ? WIN + depth : s.over.winner === "you" ? -WIN - depth : 0
  if (depth <= 0) return evaluate(s.board)
  const moves = candidates(s, 14)
  if (s.turn === "cpu") {
    let best = -Infinity
    for (const m of moves) { best = Math.max(best, search(play(s, m), depth - 1, alpha, beta)); alpha = Math.max(alpha, best); if (alpha >= beta) break }
    return best
  }
  let best = Infinity
  for (const m of moves) { best = Math.min(best, search(play(s, m), depth - 1, alpha, beta)); beta = Math.min(beta, best); if (beta <= alpha) break }
  return best
}

/** The computer's move. Easy picks among the moves that go forward at all; Medium looks one move ahead; Hard four. */
export function computerMove(s: CCState, level: Level, rand: () => number = Math.random): Move {
  const moves = legalMoves(s)
  const sign = s.turn === "cpu" ? 1 : -1
  if (level === "easy") {
    const fwd = moves.filter((m) => gain(s, m) > 0)
    const pool = fwd.length && rand() < 0.8 ? fwd : moves
    return pool[Math.floor(rand() * pool.length)]!
  }
  const depth = level === "hard" ? 4 : 1
  let best: Move[] = [], bestV = -Infinity
  for (const m of candidates(s, level === "hard" ? 24 : 200)) {
    const after = play(s, m)
    let v = sign * search(after, depth - 1, -Infinity, Infinity)
    v += gain(s, m) * 0.01 // between equals, the longer stride
    if (v > bestV + 1e-9) { bestV = v; best = [m] } else if (Math.abs(v - bestV) <= 1e-9) best.push(m)
  }
  return best[Math.floor(rand() * best.length)]!
}

/** Hint: what Medium would play for you. */
export const hintMove = (s: CCState): Move => computerMove(s, "medium", () => 0)

/** Names a hole for people: its row from the top (1 to 17) and its place in that row from the left. */
export function holeName(n: number): string {
  const r = HOLES[n]!.r
  const row = HOLES.filter((h) => h.r === r)
  return `row ${r + 9}, hole ${row.findIndex((h) => h === HOLES[n]) + 1} of ${row.length}`
}

/** Where to draw hole n, as percentages of the board's width and height (the board is 13 by about 14.86 units). */
export const BOARD_W = 13, BOARD_H = 16 * (Math.sqrt(3) / 2) + 1
export function place(n: number): [number, number] {
  const { q, r } = HOLES[n]!
  return [((q + r / 2 + 6.5) / BOARD_W) * 100, ((r * (Math.sqrt(3) / 2) + BOARD_H / 2) / BOARD_H) * 100]
}
